import { IconButton } from '@utilitywarehouse/hearth-react';
import {
  AddSmallIcon,
  MinusSmallIcon,
} from '@utilitywarehouse/hearth-react-icons';
import { Slider as RadixSlider } from 'radix-ui';
import {
  type ReactNode,
  useCallback,
  useEffect,
  useEffectEvent,
  useState,
} from 'react';

import './styles.scss';

interface Slider {
  onChange: (value: number) => void;
  value: number;
  name: string;
  disabled?: boolean;
  max: number;
  min?: number;
  step?: number;
}

const Slider = ({
  onChange,
  value,
  name,
  disabled = false,
  max = 100,
  step = 1,
}: Slider) => {
  const [thumbOffset, setThumbOffset] = useState(0);

  // by default, the Radix Range element is set to use simple
  // percentage to scale. However, the Thumb element includes
  // an offset based on it's width, the intent as far as I can
  // tell is to ensure it does not overflow the edge of the Track
  // at the extent of it's travel. This means the Range will reach
  // the Thumb's left and right edges the closer it is to min and max
  // values. When border radius is included, it will cause a visible
  // over/underflow which looks bad.
  // This below nonsense calculates the same offset, which is added
  // to the Range's scale to fix this.
  const rangeOffsetPerc = 100 - (value / max) * 100;

  const getThumbOffset = (width: number, perc: number) => {
    const diffFromFifty = Math.abs(perc - 50);
    if (perc <= 50) return width * (diffFromFifty / 100);
    return -(width * (diffFromFifty / 100));
  };

  // use a callback ref here to ensure that the "Thumb" has
  // loaded before attempting to calculate the resulting DOM
  // node's dimensions. Otherwise on first load it
  // will be undefined. Using a useCallback should prevent recreating
  // this function on all but the first render
  const thumbRef = useCallback(
    (node: HTMLElement) => {
      if (node !== null) {
        const thumbWidth = node.offsetWidth;
        setThumbOffset(getThumbOffset(thumbWidth, rangeOffsetPerc));
      }
    },
    [rangeOffsetPerc]
  );

  return (
    <RadixSlider.Root
      className="slider__Root"
      name={name}
      value={[value]}
      disabled={disabled}
      max={max}
      step={step}
      onValueChange={([value]) => onChange(value)}
    >
      <RadixSlider.Track className="slider__Track">
        <RadixSlider.Range className="slider__Range" asChild>
          <span
            style={{
              right: `calc(${rangeOffsetPerc}% + ${thumbOffset}px)`,
            }}
          />
        </RadixSlider.Range>
      </RadixSlider.Track>
      <RadixSlider.Thumb
        className="slider__Thumb"
        aria-label="Value"
        ref={thumbRef}
      />
    </RadixSlider.Root>
  );
};

interface SliderIncrement {
  onHold: (held: boolean) => void;
  onClick: () => void;
  label: string;
  disabled?: boolean;
  children: ReactNode;
}

const SliderIncrement = ({
  onHold,
  onClick,
  label,
  disabled = false,
  children,
}: SliderIncrement) => {
  return (
    <IconButton
      variant="ghost"
      colorScheme="functional"
      size="sm"
      label={label}
      type="button"
      disabled={disabled}
      onClick={onClick}
      onPointerDown={() => onHold(true)}
      onPointerUp={() => onHold(false)}
      onPointerLeave={() => onHold(false)}
    >
      {children}
    </IconButton>
  );
};

interface SliderCompositionProps extends Slider {
  /** default interval in ms to increment when holding the increment buttons */
  holdRate?: number;
  /**
   * lower bound for the hold rate to reduce to while continuing
   * to hold the buttons
   */
  holdLimit?: number;
  /**
   * number of value changes before the holdRate value will halve
   */
  holdInterval?: number;
}

const SliderComposition = ({
  value,
  name,
  disabled = false,
  max,
  min = 0,
  onChange,
  step = 1,
  holdRate = 250,
  holdLimit = 50,
  holdInterval = 5,
}: SliderCompositionProps) => {
  const [increments, setIncrements] = useState(0);
  const [intervalMs, setIntervalMs] = useState(holdRate);
  const [held, setHeld] = useState(0);
  const [holdContinue, setHoldContinue] = useState(false);

  const handleInterval = useEffectEvent((increment: number) => {
    const numericValue = Number(value);
    // condition to stop continuous increments when we reach the
    // start and end of the track limits
    if (numericValue + increment <= max && numericValue + increment >= min) {
      setIncrements(increments + 1);
      onChange(numericValue + increment);
    } else if (numericValue + increment > max) {
      // if new value overflows, set to min or max depending
      // on value change direction. This is to avoid having a
      // value not divisible by the step causing it to halt
      // before it reaches the end.
      onChange(max);
      setIncrements(increments + 1);
    } else if (numericValue + increment < min) {
      onChange(min);
      setIncrements(increments + 1);
    }

    // every set number of increments, half the interval time until
    // a defined lower limit is reached. Required increments
    // are doubled in line with the reduced interval, so the apparent
    // time between increased increment speed is the same.
    if (
      increments > 0 &&
      (holdInterval * (holdRate / intervalMs)) / increments === 1 &&
      intervalMs >= holdLimit
    ) {
      setIntervalMs(intervalMs / 2);
    }
  });

  const handleChange = (change: number) => {
    const newValue = Number(value) + change;
    if (newValue <= max && newValue >= min) {
      return onChange(newValue);
    }
  };

  useEffect(() => {
    let interval: ReturnType<typeof setInterval>;
    let timeout: ReturnType<typeof setTimeout>;

    if (held !== 0) {
      // timeout to avoid double calling when just clicking
      // rather than holding
      timeout = setTimeout(() => {
        setHoldContinue(true);
      }, holdRate);

      if (holdContinue) {
        interval = setInterval(() => {
          handleInterval(held);
        }, intervalMs);
      }
    }

    if (held === 0) {
      setHoldContinue(false);
      setIncrements(0);
      setIntervalMs(holdRate);
    }

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [value, step, held, increments, intervalMs, holdRate, holdContinue]);

  return (
    <div className="slider__Container">
      <SliderIncrement
        label="Decrease"
        disabled={disabled}
        onHold={(isHeld) => setHeld(isHeld ? -step : 0)}
        onClick={() => handleChange(-step)}
      >
        <MinusSmallIcon />
      </SliderIncrement>
      <Slider
        value={value}
        name={name}
        disabled={disabled}
        max={max}
        step={step}
        onChange={onChange}
      />
      <SliderIncrement
        label="Increase"
        disabled={disabled}
        onHold={(isHeld) => setHeld(isHeld ? step : 0)}
        onClick={() => handleChange(step)}
      >
        <AddSmallIcon />
      </SliderIncrement>
    </div>
  );
};

export { SliderComposition as Slider };
export { type SliderCompositionProps as SliderProps };
